import type { HarnessPick } from './squad';
import type { FplPosition } from '@lib/live/team/types';
import type { MatchSnapshot } from '@lib/live/match/types';

export type RosterPlayer = {
  optaId: string;
  fplId: number;
  displayName: string;
  position: FplPosition;
  teamShortName: string;
  matchId: string;
  /** Minutes and points already recorded, so a squad can be built from
   * players who actually scored rather than from whoever comes first. */
  minutesPlayed: number;
  points: number;
};

export type Roster = {
  matchId: string;
  players: RosterPlayer[];
  /**
   * Players on the team sheet with no FPL identity, and so not selectable.
   *
   * Surfaced rather than silently dropped: on a real simulated fixture this is
   * around a sixth of the roster, and a picker that quietly showed fewer
   * players than the match holds would look like it had lost them.
   */
  unidentifiedCount: number;
};

const FPL_POSITIONS: readonly string[] = ['GK', 'DEF', 'MID', 'FWD'];

const isFplPosition = (value: string): value is FplPosition =>
  FPL_POSITIONS.includes(value);

/**
 * The selectable players in a match, drawn from the engine's own team sheet.
 *
 * Building a squad out of the match's real roster is what keeps the harness
 * honest: every player is one the engine is actually publishing a channel for,
 * with the Opta and FPL ids the pitch joins on, so nothing about the squad is
 * invented beyond the choice of who is in it.
 *
 * A player with no FPL id is excluded. `LiveTeamPlayer.id` is that id, and the
 * availability rules fall back to matching on it, so two such players would
 * collide on an empty value and read as the same pick.
 */
export const toRoster = (snapshot: MatchSnapshot): Roster => {
  const teamNames = new Map([
    [snapshot.homeTeam.id, snapshot.homeTeam.name],
    [snapshot.awayTeam.id, snapshot.awayTeam.name]
  ]);

  const players: RosterPlayer[] = [];
  let unidentifiedCount = 0;

  for (const player of snapshot.players) {
    const fplId = Number(player.fplId);
    if (!player.fplId || Number.isNaN(fplId)) {
      unidentifiedCount += 1;
      continue;
    }
    if (!isFplPosition(player.position)) {
      unidentifiedCount += 1;
      continue;
    }

    players.push({
      optaId: player.playerId,
      fplId,
      displayName: player.name,
      position: player.position,
      teamShortName: teamNames.get(player.teamId) ?? '',
      matchId: snapshot.matchId,
      minutesPlayed: player.minutesPlayed,
      points: player.points
    });
  }

  return { matchId: snapshot.matchId, players, unidentifiedCount };
};

/**
 * The same player across every selected fixture, so a pick built from a squad
 * spanning two simulated matches carries both ids and exercises the
 * double-gameweek aggregation rather than only one leg of it.
 */
export const toHarnessPick = (
  player: RosterPlayer,
  slot: number,
  rosters: Roster[]
): HarnessPick => ({
  optaId: player.optaId,
  fplId: player.fplId,
  displayName: player.displayName,
  position: player.position,
  teamShortName: player.teamShortName,
  slot,
  optaMatchIds: rosters
    .filter((roster) =>
      roster.players.some((one) => one.optaId === player.optaId)
    )
    .map((roster) => roster.matchId)
});
